/**
 * On-hit lifeSteal + spellVamp heal rules (FID-CBT-03 / SPY-1407).
 * Eligibility residual: FID-CBT-04 / SPY-1606.
 * Type-string lock: FID-CBT-05 / SPY-1706 (exact lowercase only; no normalize).
 * Eligible-type list: FID-CBT-06 / SPY-1906 ('true' never vamps).
 * Overheal/dead lock residual: SPY-1608 / SPY-1708 (near-full clamp + dead refuse).
 * Missing maxHp does not trip the full-HP lock (SPY-1908).
 * Non-finite attacker hp never vamps (FID-CBT-07 / SPY-2006).
 * Non-finite damage never vamps (SPY-2008).
 */

/** Already-full HP skips vamp (no overheal credit). */
export const VAMP_OVERHEAL_LOCK = true;

/** Exact lowercase types that may life-steal / spell-vamp (FID-CBT-06). */
export const VAMP_ELIGIBLE_DAMAGE_TYPES = Object.freeze(['physical', 'magic']);

/**
 * Only exact lowercase 'physical' | 'magic' may vamp.
 * Case variants, whitespace, and 'true'/'True'/'PHYSICAL' are refused (no normalize).
 */
export function isVampEligibleDamageType(type) {
  return VAMP_ELIGIBLE_DAMAGE_TYPES.includes(type);
}

/** Physical damage → lifeSteal heal. */
export function lifeStealHealAmount(damageDealt, lifeStealRatio = 0) {
  if (!(damageDealt > 0) || !(lifeStealRatio > 0)) return 0;
  return Math.round(damageDealt * lifeStealRatio);
}

/** Magic (ability) damage → spellVamp heal. */
export function spellVampHealAmount(damageDealt, spellVampRatio = 0) {
  if (!(damageDealt > 0) || !(spellVampRatio > 0)) return 0;
  return Math.round(damageDealt * spellVampRatio);
}

/**
 * Apply vamp heal to attacker after mitigated damage lands.
 * @returns {number} hp healed
 */
export function applyVampHealFromDamage(attacker, effectiveDamage, damageType) {
  if (!attacker || attacker.isDead || !Number.isFinite(effectiveDamage) || !(effectiveDamage > 0)) {
    return 0;
  }
  if (!isVampEligibleDamageType(damageType)) return 0;
  const before = attacker.hp ?? 0;
  if (!Number.isFinite(before)) return 0;
  const hasMax = Number.isFinite(attacker.maxHp);
  if (VAMP_OVERHEAL_LOCK && hasMax && before >= attacker.maxHp) return 0;
  let heal = 0;
  if (damageType === 'physical') {
    heal = lifeStealHealAmount(effectiveDamage, attacker.lifeSteal || 0);
  } else if (damageType === 'magic') {
    heal = spellVampHealAmount(effectiveDamage, attacker.spellVamp || 0);
  }
  if (heal <= 0) return 0;
  const next = before + heal;
  attacker.hp = hasMax ? Math.min(attacker.maxHp, next) : next;
  return attacker.hp - before;
}
